// ============================================================================
// ManualCollectionCard — Simple direct-select card with media thumbnail
// ============================================================================

import React, { memo } from 'react';
import { useAppSelector } from '../../redux/hooks';
import type { Collection } from '../../types/collections.types';

interface ManualCollectionCardProps {
  collection: Collection;
  isSelected: boolean;
  onSelect: (pkId: string) => void;
}

const ManualCollectionCard: React.FC<ManualCollectionCardProps> = memo(({
  collection,
  isSelected,
  onSelect,
}) => {
  const productCount = collection.products?.length || 0;

  // Media thumbnail from Redux (fallback to collection.image)
  const mediaUrl = useAppSelector(
    (s) => s.collections.collectionMedia[collection.pk_id]?.image_url
  );
  const mediaStatus = useAppSelector(
    (s) => s.collections.collectionMedia[collection.pk_id]?.image_status
  );
  const thumbnail =
    mediaStatus === 'completed' && mediaUrl
      ? mediaUrl
      : collection.image || 'https://via.placeholder.com/40';

  return (
    <button
      onClick={() => onSelect(collection.pk_id)}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-150 cursor-pointer relative ${
        isSelected ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'
      }`}
    >
      {isSelected && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-[#EE4423]" />
      )}

      <img
        src={thumbnail}
        alt={collection.title}
        className="w-10 h-10 rounded-lg object-cover bg-gray-800 flex-shrink-0"
        loading="lazy"
      />

      <div className="flex-1 min-w-0">
        <h4 className={`text-[13px] line-clamp-1 leading-tight ${
          isSelected ? 'text-white font-semibold' : 'text-gray-300 font-medium'
        }`}>
          {collection.title}
        </h4>
        <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
          {productCount} Product{productCount !== 1 ? 's' : ''}
        </p>
      </div>
    </button>
  );
});

ManualCollectionCard.displayName = 'ManualCollectionCard';
export default ManualCollectionCard;
